import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { PageHero } from "@/components/PublicLayout";
import { TextField, TextAreaField } from "@/components/FormKit";
import { SuccessScreen } from "@/pages/BookRide";
import { useSite, phoneHref, whatsappHref } from "@/context/SiteContext";
import { WhatsappLogo } from "@phosphor-icons/react";
import { COMPANY } from "@/lib/data";

const empty = { name: "", phone: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const content = useSite();
  const setE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (done) return <SuccessScreen title="Message Sent" message="Thank you for reaching out! A member of the CareDeserved team will get back to you soon." />;

  const info = [
    { icon: "Phone", label: "Phone", value: content.phone, href: phoneHref(content.phone) },
    { icon: "ChatCircleText", label: "Email", value: content.email, href: `mailto:${content.email}` },
    { icon: "MapPinLine", label: "Website", value: content.website },
  ];

  const waEnabled = content.whatsapp_enabled !== false && (content.whatsapp_number || "").trim();

  return (
    <>
      <PageHero eyebrow="Contact Us" title="We're Here to Help"
        subtitle="Have a question about transportation, a partnership, or a scheduled ride? Reach out and our team will be glad to assist." />
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">CareDeserved LLC</h2>
              {waEnabled && (
                <div data-testid="contact-whatsapp" className="mt-6 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/5 p-5">
                  <div className="flex items-start gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#25D366] text-white">
                      <WhatsappLogo size={26} weight="fill" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#1a8f4c]">WhatsApp — Fastest way to reach our team</p>
                      <a href={whatsappHref(content)} target="_blank" rel="noopener noreferrer"
                        data-testid="contact-whatsapp-link"
                        className="font-heading text-lg font-bold text-brand-blue hover:text-[#1ebe5d]">
                        {content.whatsapp_number}{content.whatsapp_username ? ` | ${content.whatsapp_username}` : ""}
                      </a>
                    </div>
                  </div>
                  <a href={whatsappHref(content)} target="_blank" rel="noopener noreferrer"
                    data-testid="contact-whatsapp-cta"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1ebe5d]">
                    <WhatsappLogo size={18} weight="fill" /> Chat on WhatsApp
                  </a>
                  <p className="mt-3 text-xs leading-relaxed text-slate-500">
                    For medical, ODP referral, or other sensitive information, please use our secure online forms.
                  </p>
                </div>
              )}
              <div className="mt-6 space-y-4">
                {info.map((i) => (
                  <div key={i.label} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-brand-bg p-5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
                      <Icon name={i.icon} size={24} weight="duotone" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{i.label}</p>
                      {i.href ? (
                        <a href={i.href} className="font-heading text-lg font-bold text-brand-blue hover:text-brand-purple">{i.value}</a>
                      ) : (
                        <p className="font-heading text-lg font-bold text-brand-blue">{i.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-2xl bg-brand-blue p-6 text-white noise-overlay">
                <p className="font-heading text-lg font-semibold">Need a return ride?</p>
                <p className="mt-1 text-sm text-slate-300">If you can't reach your assigned driver, call our main office and we'll help right away.</p>
              </div>
              {content.office_hours && (
                <div data-testid="contact-office-hours" className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={20} weight="duotone" className="text-brand-purple" />
                    <p className="font-heading text-base font-semibold text-slate-900">Office Hours</p>
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-slate-600">
                    {content.office_hours.split("\n").map((line, i) => <p key={i}>{line}</p>)}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField label="Name" required testid="contact-name" value={form.name} onChange={setE("name")} />
                <TextField label="Phone" testid="contact-phone" type="tel" value={form.phone} onChange={setE("phone")} />
                <TextField label="Email" required testid="contact-email" type="email" wrapClass="sm:col-span-2" value={form.email} onChange={setE("email")} />
                <TextField label="Subject" testid="contact-subject" wrapClass="sm:col-span-2" value={form.subject} onChange={setE("subject")} />
                <TextAreaField label="Message" required testid="contact-message" wrapClass="sm:col-span-2" value={form.message} onChange={setE("message")} />
              </div>
              <Button type="submit" disabled={loading} data-testid="contact-submit"
                className="mt-6 h-14 w-full rounded-full bg-brand-purple text-base font-semibold text-white hover:bg-brand-purple2">
                {loading ? "Sending…" : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
